import { Component, inject, Inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import Swal from 'sweetalert2';
import { BarrioServices } from '../../../../core/services/barrio.service';

@Component({
  selector: 'app-barrios-municipio-dialog',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogContent,
  ],
  templateUrl: './barrios-municipio-dialog.html',
  styleUrls: ['./barrios-municipio-dialog.scss'],
})
export class BarriosMunicipioDialog implements OnInit {
  private fb = inject(FormBuilder);

  barrios: any[] = [];
  cargando = false;
  guardando = false;
  barrioEnEdicion: any = null;

  form = this.fb.group({
    nombre: ['', [Validators.required, Validators.maxLength(100)]],
    acronimo: ['', [Validators.required, Validators.maxLength(10)]],
  });

  constructor(
    private dialogRef: MatDialogRef<BarriosMunicipioDialog>,
    private barrioService: BarrioServices,
    @Inject(MAT_DIALOG_DATA) public data: { municipio: any },
  ) {}

  get municipio(): any {
    return this.data.municipio;
  }

  ngOnInit(): void {
    this.cargarBarrios();
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  cargarBarrios(): void {
    this.cargando = true;

    this.barrioService.obtenerBarriosPorMunicipio(this.municipio.id).subscribe({
      next: (respuesta) => {
        this.barrios = Array.isArray(respuesta) ? respuesta : (respuesta?.data ?? []);
        this.cargando = false;
      },
      error: (error) => {
        this.cargando = false;

        Swal.fire({
          icon: 'error',
          title: 'No se pudieron cargar los barrios',
          text: error?.error?.message ?? 'Ocurrió un error al obtener los barrios.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  editar(barrio: any): void {
    this.barrioEnEdicion = barrio;
    this.form.patchValue({
      nombre: barrio.nombre ?? '',
      acronimo: barrio.acronimo ?? '',
    });
  }

  cancelarEdicion(): void {
    this.barrioEnEdicion = null;
    this.form.reset({ nombre: '', acronimo: '' });
  }

  guardar(): void {
    if (this.guardando) return;

    const valores = this.form.getRawValue();
    const nombre = (valores.nombre ?? '').trim();
    const acronimo = (valores.acronimo ?? '').trim().toUpperCase();

    // Evita aceptar espacios en blanco como valor válido
    if (!nombre) this.form.controls.nombre.setErrors({ required: true });
    if (!acronimo) this.form.controls.acronimo.setErrors({ required: true });

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const duplicado = this.barrios.some(
      (b) =>
        b.id !== this.barrioEnEdicion?.id &&
        String(b.nombre ?? '')
          .trim()
          .toLowerCase() === nombre.toLowerCase(),
    );

    if (duplicado) {
      this.form.controls.nombre.setErrors({ duplicado: true });
      this.form.markAllAsTouched();
      return;
    }

    this.guardando = true;
    const editando = !!this.barrioEnEdicion;

    const peticion = editando
      ? this.barrioService.actualizarBarrio(this.barrioEnEdicion.id, { nombre, acronimo })
      : this.barrioService.crearBarrio({ nombre, acronimo, municipio: this.municipio.id });

    peticion.subscribe({
      next: () => {
        this.guardando = false;
        this.cancelarEdicion();
        this.cargarBarrios();

        Swal.fire({
          // toast: true,
          // position: 'top-end',
          icon: 'success',
          title: editando ? 'Barrio actualizado' : 'Barrio agregado',
          timer: 1200,
          showConfirmButton: false,
        });
      },
      error: (error) => {
        this.guardando = false;

        Swal.fire({
          icon: 'error',
          title: 'No se pudo guardar',
          text:
            error?.error?.message?.join?.(', ') ??
            error?.error?.message ??
            'Ocurrió un error al guardar el barrio.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  eliminar(barrio: any): void {
    Swal.fire({
      title: '¿Eliminar barrio?',
      text: `Se eliminará "${barrio.nombre}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;

      this.barrioService.eliminarBarrio(barrio.id).subscribe({
        next: () => {
          if (this.barrioEnEdicion?.id === barrio.id) {
            this.cancelarEdicion();
          }
          this.cargarBarrios();
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar el barrio.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }
}
